import { useEffect, useCallback } from 'react';
import { useMediaQuery } from '@mui/material';
import { colors } from '../../../options/colors';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import NoResultsText from '../../Typography/NoResultsText/NoResultsText';

// Helpers
import generatePlaceholderArray from '../../../utils/helpers/generatePlaceholderArray';

// Register annotationPlugin to enable drawing annotation lines
Chart.register(annotationPlugin);

const PrintBarChart = ({ id, labels, data, handleClick, isDrillStats, selectedSession, selectedDataset }) =>
{
  // Dynamically display chart based on breakpoint
  const matches = true;
  const chartId = 'barChart';
  const createChart = useCallback((datasets, isMobile, isDrills, selSession, datasetIdx) =>
  {
    // Get canvas by ID - allows rendering different chart for desktop and mobiles to set different font sizes

    const ctx = document.getElementById(chartId).getContext('2d');
    const sessionIdx = false;

    let elDetails = [];
    const highlightInitialBar = {
      id: 'initialBar',
      beforeDraw: chart =>
      {
        if (elDetails.length === 0 && chart._metasets.length > 0)
        {
          // Highlight bar for latest session by default on char initialisation
          const initialBar = chart._metasets[datasetIdx].data[sessionIdx];
          if (initialBar)
          {
            initialBar.options.backgroundColor = colors.secondaryOrange;
            elDetails.push({
              datasetIndex: 0,
              elIndex: 9,
              el: initialBar,
              originalColor: datasets[0].color,
            });
          }
        }
      },
    };

    // Create data object for chart config - iterate through datasets and generate dataset for each record
    const sets = [];
    const annotations = [];
    datasets.forEach((dataset, idx) =>
    {
      // Create an array with length of 10 and fill all values with dataset.color - enables updating bg colour on click
      // Set initial bar colour for first dataset to secondary orange for index stats bar chart
      if (!dataset)
      {
        return;
      }
      const backgroundColor = generatePlaceholderArray(10, '#f6bfbf'); //dataset.color);

      backgroundColor[backgroundColor.length - 1] = '#da0000';

      sets.push({
        data: dataset.data,
        backgroundColor: backgroundColor,
        borderRadius: 0,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      });

      annotations.push({
        adjustScaleRange: true,
        drawTime: 'afterDatasetsDraw',
        type: 'line',
        scaleID: isMobile ? 'x' : 'y',
        value: dataset.avgScore,
        borderColor: '#da0000', //dataset.avgColor,
        borderWidth: 3,
      });
    });

    const data = {
      labels: isDrills ? labels : labels[datasetIdx],
      datasets: sets,
    };

    // Create chart config object
    const config = {
      type: 'bar',
      data,
      options: {
        animation: {
          onComplete: done,
          duration: 0
        },
        maintainAspectRatio: false,
        indexAxis: isMobile ? 'y' : 'x',
        borderSkipped: 'false',
        plugins: {
          // Hide legend
          legend: {
            display: false,
          },
          // Hide hover tooltip
          tooltip: {
            enabled: false,
          },
          autocolors: false,
          // Draw annotation lines
          annotation: {
            annotations,
          },
        },
        scales: {
          x: {
            max: 10,
            grid: {
              color: 'transparent',
              borderColor: 'transparent',
              tickColor: 'transparent',
            },
            ticks: {
              color: 'black',
              padding: 0,
              font: {
                family: 'RobotoRegular, sans-serif',
                color: 'black',
                size: 9,
              },
            },
          },
          y: {
            max: 100,
            position: 'right',
            beginAtZero: true,
            grid: {
              color: 'transparent',
              borderColor: 'transparent',
              tickColor: 'transparent',
            },
            ticks: {
              display: true,
              callback: function (value, index, ticks)
              {
                if (ticks.length - 1 === index)
                {
                  return value;
                }

              },
              major: true,
              color: 'black',
              padding: 0,
              left: -5,
              font: {
                family: 'RobotoRegular, sans-serif',
                color: 'black',
                size: 14,
              },
            },
          },
        },
      },
      plugins: [!isDrills && highlightInitialBar],
    };

    // Instantiate chart with config object
    new Chart(ctx, config);
    function done()
    {
      var url = this.toBase64Image();
      let el = document.getElementById(id + 'ChartImage');
      if (el)
      {
        el.src = url;
        document.getElementById(`${id}ChartContainer`).style.display = 'none';
      }

    }


  }, [labels, handleClick]);

  useEffect(() =>
  {
    if (data && data.length > 0)
    {
      // Remove existing canvas before re-drawing to resolve error when parent re-renders
      const canvas =
        `<canvas id="${chartId}"/>`;
      const chart = document.getElementById(chartId);
      if (chart) chart.remove();
      const chartContainer = document.getElementById(`${id}ChartContainer`);
      chartContainer.innerHTML = canvas;
      createChart(data, !matches, isDrillStats, selectedSession, selectedDataset);
    }
  }, [id, createChart, data, matches, isDrillStats, selectedSession, selectedDataset]);

  return (
    <>
      {
        (!data || data.length === 0) &&
        <NoResultsText text={'No chart data'} />
      }
    </>
  );
};

export default PrintBarChart;
